import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, RefreshCw, CheckCircle, AlertTriangle, Link2, XCircle } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const statusLabels = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-600' },
  auto_matched: { label: 'Otomatik', color: 'bg-blue-600' },
  approved: { label: 'Onaylandi', color: 'bg-green-600' },
  corrected: { label: 'Duzeltildi', color: 'bg-purple-600' },
  rejected: { label: 'Reddedildi', color: 'bg-red-600' },
};

const ServiceMappingTab = () => {
  const [mappings, setMappings] = useState([]);
  const [stats, setStats] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState('');

  const token = getFromLocalStorage('accessToken');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const loadData = async () => {
    try {
      const [mapRes, svcRes, catRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/service-mappings`, { headers }),
        fetch(`${BACKEND_URL}/api/service-catalog/services`, { headers }),
        fetch(`${BACKEND_URL}/api/service-catalog/categories`, { headers }),
      ]);
      if (mapRes.ok) { const d = await mapRes.json(); setMappings(d.mappings || []); setStats(d.stats || {}); }
      if (svcRes.ok) { const d = await svcRes.json(); setCatalog(d.services || []); }
      if (catRes.ok) { const d = await catRes.json(); setCategories(d.categories || []); }
    } catch (e) { console.error('Load service mapping data error:', e); }
  };

  useEffect(() => { loadData(); }, []);

  const runBackfill = async () => {
    setBackfillLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-mappings/backfill`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        toast({ title: "Backfill tamamlandi", description: `${d.created} yeni eslesme olusturuldu, ${d.skipped} atlandı` });
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Backfill hatasi');
      }
    } catch (err) { toast({ title: "Hata", description: err.message, variant: "destructive" }); }
    setBackfillLoading(false);
  };

  const batchApprove = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-mappings/batch-approve`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        toast({ title: "Toplu onay", description: `${d.approvedCount} eslesme onaylandi` });
        await loadData();
      }
    } catch (e) { console.error('Batch approve error:', e); }
    setLoading(false);
  };

  const updateMapping = async (mappingId, newServiceId, newStatus) => {
    setLoading(true);
    try {
      const body = {};
      if (newServiceId) body.matchedServiceId = newServiceId;
      if (newStatus) body.status = newStatus;
      const res = await fetch(`${BACKEND_URL}/api/service-mappings/${mappingId}`, {
        method: 'PATCH', headers, body: JSON.stringify(body),
      });
      if (res.ok) {
        // Optimistic update: immediately update local state
        if (newStatus) {
          setMappings(prev => prev.map(m => m.id === mappingId ? { ...m, status: newStatus } : m));
          // Recompute stats from updated mappings
          setMappings(prev => {
            const updated = prev.map(m => m.id === mappingId
              ? { ...m, status: newStatus, matchedServiceId: newServiceId || m.matchedServiceId }
              : m);
            setStats({
              total: updated.length,
              pending: updated.filter(m => m.status === 'pending').length,
              autoMatched: updated.filter(m => m.status === 'auto_matched').length,
              approved: updated.filter(m => m.status === 'approved').length,
              corrected: updated.filter(m => m.status === 'corrected').length,
            });
            return updated;
          });
        }
        const statusLabel = newStatus === 'approved' ? 'Onaylandi' : newStatus === 'corrected' ? 'Duzeltildi' : newStatus === 'rejected' ? 'Reddedildi' : 'Guncellendi';
        toast({ title: statusLabel, description: `Eslestirme basariyla ${statusLabel.toLowerCase()}` });
        setEditMapping(null);
        // Full refresh for data consistency
        await loadData();
      }
    } catch (e) { console.error('Update mapping error:', e); }
    setLoading(false);
  };

  const filtered = mappings.filter(m => {
    if (filterStatus !== 'all' && m.status !== filterStatus) return false;
    if (search) {
      const term = search.toLowerCase();
      if (!m.sourceText.toLowerCase().includes(term) && !(m.matchedServiceName || '').toLowerCase().includes(term)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="service-mapping-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-white font-semibold text-sm flex items-center gap-2">
          <Link2 className="h-4 w-4 text-purple-400" />Legacy Hizmet Eslestirme
        </h2>
        <div className="flex gap-2">
          <Button size="sm" onClick={runBackfill} disabled={backfillLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7" data-testid="backfill-btn">
            {backfillLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Backfill
          </Button>
          <Button size="sm" onClick={batchApprove} disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid="batch-approve-btn">
            <CheckCircle className="h-3 w-3 mr-1" />Toplu Onayla
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: 'Toplam', value: stats.total || 0, color: 'text-white' },
          { label: 'Bekliyor', value: stats.pending || 0, color: 'text-yellow-400' },
          { label: 'Otomatik', value: stats.autoMatched || 0, color: 'text-blue-400' },
          { label: 'Onaylandi', value: stats.approved || 0, color: 'text-green-400' },
          { label: 'Duzeltildi', value: stats.corrected || 0, color: 'text-purple-400' },
        ].map(s => (
          <Card key={s.label} className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
            <p className={`font-bold text-lg ${s.color}`}>{s.value}</p>
            <p className="text-gray-500 text-[10px]">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ara..."
            className="bg-gray-900 border-gray-700 text-white text-xs pl-7 h-8" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-gray-900 border border-gray-700 text-white rounded text-xs p-1">
          <option value="all">Tum Durumlar</option>
          <option value="pending">Bekliyor</option>
          <option value="auto_matched">Otomatik</option>
          <option value="approved">Onaylandi</option>
          <option value="corrected">Duzeltildi</option>
          <option value="rejected">Reddedildi</option>
        </select>
      </div>

      {/* Mapping list */}
      {filtered.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          {mappings.length === 0 ? 'Henuz eslesme yok. Backfill butonunu kullanin.' : 'Filtre sonucu bos.'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(m => {
            const st = statusLabels[m.status] || { label: m.status, color: 'bg-gray-600' };
            return (
              <div key={m.id} className="bg-gray-800/70 border border-gray-700/40 rounded-lg p-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-xs font-medium">"{m.sourceText}"</span>
                    <Badge className={`${st.color} text-white text-[9px]`}>{st.label}</Badge>
                    <span className="text-gray-600 text-[10px]">{m.occurrenceCount}x</span>
                    {m.confidenceScore > 0 && (
                      <span className={`text-[10px] ${m.confidenceScore >= 0.8 ? 'text-green-500' : m.confidenceScore >= 0.5 ? 'text-yellow-500' : 'text-red-500'}`}>
                        %{Math.round(m.confidenceScore * 100)}
                      </span>
                    )}
                  </div>
                  {m.matchedServiceName && (
                    <p className="text-brand text-[10px] mt-0.5">
                      {m.matchedServiceName} <span className="text-gray-600">({m.categoryName})</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  {m.status !== 'approved' && (
                    <Button size="sm" onClick={() => updateMapping(m.id, null, 'approved')}
                      className="bg-green-700 hover:bg-green-800 text-white text-[10px] h-6 px-2">
                      <CheckCircle className="h-2.5 w-2.5" />
                    </Button>
                  )}
                  <Button size="sm" onClick={() => { setEditMapping(m); setSelectedServiceId(m.matchedServiceId || ''); }}
                    className="bg-gray-700 hover:bg-gray-600 text-white text-[10px] h-6 px-2">
                    <AlertTriangle className="h-2.5 w-2.5" />
                  </Button>
                  {m.status !== 'rejected' && (
                    <Button size="sm" onClick={() => updateMapping(m.id, null, 'rejected')}
                      className="bg-red-700 hover:bg-red-800 text-white text-[10px] h-6 px-2">
                      <XCircle className="h-2.5 w-2.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Mapping Dialog */}
      <Dialog open={!!editMapping} onOpenChange={(open) => { if (!open) setEditMapping(null); }}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader><DialogTitle className="text-white text-lg">Eslestirme Duzelt</DialogTitle></DialogHeader>
          {editMapping && (
            <div className="space-y-3">
              <div><p className="text-gray-500 text-xs">Orijinal Metin</p>
                <p className="text-white text-sm font-medium">"{editMapping.sourceText}"</p></div>
              <div><p className="text-gray-500 text-xs">Normalize Edilmis</p>
                <p className="text-gray-400 text-sm">{editMapping.normalizedText}</p></div>
              <div><p className="text-gray-500 text-xs">Kullanim Sayisi</p>
                <p className="text-white text-sm">{editMapping.occurrenceCount}x</p></div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Dogru Hizmet Sec</p>
                <select value={selectedServiceId} onChange={(e) => setSelectedServiceId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm">
                  <option value="">Eslesme yok</option>
                  {catalog.map(s => <option key={s.id} value={s.id}>{s.name} ({s.categoryName})</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditMapping(null)} variant="outline" className="flex-1 border-gray-600 text-gray-300 text-sm">Iptal</Button>
                <Button onClick={() => updateMapping(editMapping.id, selectedServiceId, 'corrected')} disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet & Duzelt'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceMappingTab;
